import { ReactNode, useEffect } from "react";
import { Stack, Button, useTheme, useMediaQuery } from "@mui/material";
import { FilterSidebar } from "@floriday/floriday-ui";
import { useAtomValue, useSetAtom } from "jotai";
import { filterSidebarAtom } from "./../../atoms/filterSidebarAtom";
import { useTranslation } from "react-i18next";
import { RefreshIcon } from "@floriday/floriday-icons";
import { FloridayUIRouterCompat } from "../FloridayUIRouterCompat.tsx";

type FilterSidebarWrapperProps = {
  children: ReactNode;
  activeFilters: number;
  onClearFilters: () => void;
};

export function FilterSidebarWrapper({
  children,
  activeFilters,
  onClearFilters,
}: Readonly<FilterSidebarWrapperProps>) {
  const { t } = useTranslation();
  const theme = useTheme();
  const open = useAtomValue(filterSidebarAtom);
  const setOpen = useSetAtom(filterSidebarAtom);

  const handleClose = () => {
    setOpen(false);
  };

  const isMobile = useMediaQuery(theme.breakpoints.down("lg"));

  // Automatically open filter on desktop, close on mobile
  useEffect(() => {
    setOpen(!isMobile);
  }, [isMobile, setOpen]);

  return (
    <FloridayUIRouterCompat>
      <FilterSidebar
        open={open}
        filterCount={activeFilters}
        onClose={handleClose}
        onClearFilters={onClearFilters}
        variant={isMobile ? "temporary" : "persistent"}
        sx={{
          width: isMobile ? "100%" : 360,
          flexShrink: 0,
        }}
        slotProps={{
          paper: {
            sx: {
              width: isMobile ? "100%" : 360,
              maxWidth: isMobile ? "100%" : 360,
              boxSizing: "border-box",
              p: 2,
            },
          },
        }}
      >
        <Stack spacing={2} sx={{ pb: 2 }}>
          {children}
        </Stack>
        <Stack direction="row" spacing={2} mt={2}>
          <Button
            fullWidth
            variant="text"
            startIcon={<RefreshIcon />}
            onClick={onClearFilters}
          >
            {t("common.resetFilters")}
          </Button>
          <Button
            fullWidth
            variant="contained"
            color="primary"
            onClick={handleClose}
          >
            {t("common.hide")}
          </Button>
        </Stack>
      </FilterSidebar>
    </FloridayUIRouterCompat>
  );
}
